<script lang="ts" setup>
import { ref, watch } from 'vue'
import type { UploadFile } from 'element-plus'
import { UploadOutlinedIcon } from '@xpos/icons-svg'
import { notify } from '@/utils/notification'
import ImageCropperDialog from '@/components/ImageCropperDialog.vue'
import ImageDeleteButton from '@/components/image/ImageDeleteButton.vue'
import { fileToDataUrl } from '@/utils/misc'
import { compressImage, uploadImage, type UploadTarget } from '@/composables/useImageUpload'

const $props = withDefaults(defineProps<{
  modelValue?: string
  rootFormData?: any
  thumbnailField?: string
  enableCrop?: boolean
  aspectRatio?: number
  maxSizeMB?: number
  maxWidthOrHeight?: number
  disabled?: boolean
  uploadTarget?: UploadTarget
}>(), {
  enableCrop: false,
  maxSizeMB: 0.5,
  maxWidthOrHeight: 1280,
  disabled: false,
  uploadTarget: 'merchant',
})

const $emit = defineEmits(['change', 'update:modelValue'])
const thumbUrl = ref('')
const showCropper = ref(false)
const cropImageSrc = ref('')
const cropperRef = ref<InstanceType<typeof ImageCropperDialog>>()

const getNestedValue = (obj: any, path: string) => path.split('.').reduce((current, key) => current?.[key], obj)
const setNestedValue = (obj: any, path: string, value: any) => {
  const keys = path.split('.')
  const last = keys.pop()!
  let current = obj
  for (const key of keys) {
    if (!(key in current)) current[key] = {}
    current = current[key]
  }
  current[last] = value
}

watch(() => $props.modelValue, (newVal) => {
  if (!newVal) return
  thumbUrl.value = $props.thumbnailField && $props.rootFormData
    ? getNestedValue($props.rootFormData, $props.thumbnailField) || newVal
    : newVal
}, { immediate: true })

// Writes image and thumbnail together; empty strings clear both
const setImage = (url: string, thumbnail: string) => {
  $emit('change', url)
  $emit('update:modelValue', url)
  if ($props.thumbnailField) setNestedValue($props.rootFormData, $props.thumbnailField, thumbnail)
  thumbUrl.value = thumbnail
}

// Compresses and uploads image, then emits result and updates thumbnail
const uploadBlob = async (blob: Blob) => {
  const compressed = await compressImage(blob, { maxSizeMB: $props.maxSizeMB, maxWidthOrHeight: $props.maxWidthOrHeight })
  const res = await uploadImage(compressed, $props.uploadTarget)
  setImage(res.file_url, res.thumbnail_url)
}

const handleUpload = async (file: UploadFile) => {
  if (!file.raw) return
  if ($props.enableCrop) {
    cropImageSrc.value = await fileToDataUrl(file.raw)
    showCropper.value = true
    return
  }
  try {
    await uploadBlob(file.raw)
  } catch {
    notify.error('Upload failed')
  }
}

const handleCropConfirm = async (blob: Blob) => {
  try {
    await uploadBlob(blob)
    cropperRef.value?.close()
  } catch {
    notify.error('Upload failed')
    cropperRef.value?.onError()
  }
}
</script>

<template>
  <el-space :size="0">
    <div class="relative" v-if="thumbUrl">
      <img class="h-[45px] w-auto object-contain" :src="thumbUrl" />
      <ImageDeleteButton v-if="!disabled" @click="setImage('', '')" />
    </div>
    <el-upload class="avatar-uploader ml-[5px] mr-0" :show-file-list="false" :on-change="handleUpload" :auto-upload="false" accept="image/*" :disabled="disabled">
      <el-button type="primary" class="shrink-0 w-[50px]" :disabled="disabled"
        ><el-icon size="20"><UploadOutlinedIcon /></el-icon
      ></el-button>
    </el-upload>
    <ImageCropperDialog ref="cropperRef" v-model="showCropper" :image-src="cropImageSrc"
      :aspect-ratio="$props.aspectRatio" @confirm="handleCropConfirm" />
  </el-space>
</template>
